import {
  DefaultValues,
  FieldValues,
  Resolver,
  useForm,
  UseFormProps,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AnyObjectSchema } from 'yup';
import { recordTypeFormKey } from '../ExtraFields/logic';
import { useMemo } from 'react';
import { useDefaultRecordTypeName } from '../ExtraFields/useDefaultRecordTypeName';
import {
  ExtraFieldContextParam,
  ExtraFieldsSupportedObject,
} from '../ExtraFields';

export type AbsenceFormContext = {
  requireNote: boolean;
};

type Props<TFormValues extends FieldValues> = Omit<
  UseFormProps<TFormValues>,
  'resolver' | 'context'
> & {
  validationSchema: AnyObjectSchema;
  absenceFormContext: AbsenceFormContext;
  contextParams: ExtraFieldContextParam[];
};

const objectApiName = ExtraFieldsSupportedObject.Absence;

export const useAbsenceFieldsForm = <TFormValues extends FieldValues>({
  absenceFormContext,
  defaultValues,
  contextParams,
  validationSchema,
  ...props
}: Props<TFormValues>) => {
  const defaultRecordTypeName = useDefaultRecordTypeName(objectApiName);
  const defaultValuesWithRecordType = useMemo(() => {
    if (defaultRecordTypeName != null) {
      return {
        ...(defaultValues ?? {}),
        [recordTypeFormKey]: defaultRecordTypeName,
      } as DefaultValues<TFormValues>;
    }
    return defaultValues;
  }, [defaultRecordTypeName, defaultValues]);

  return useForm<TFormValues>({
    ...props,
    mode: 'onChange',
    defaultValues: defaultValuesWithRecordType,
    context: absenceFormContext,
    resolver: yupResolver(validationSchema) as unknown as Resolver<TFormValues>,
    shouldUnregister: true,
  });
};
